import { 
  ColumnRef
} from '../../data-grid/types';
import { 
  Query,
  AggregateOperationEnum,
  isNumericType,
  isStringType,
  isDateType,
  isBooleanType,
  TableView,
  Engine
} from '@/lib/engine';

export interface NovaHeaderProps {
  /**
   * Current query
   */
  query: Query;
  
  /**
   * Available columns
   */
  columns: ColumnRef[];
  
  /**
   * Original table columns (for aggregation)
   * These are the columns from the original table, not the result of a previous aggregation
   */
  originalColumns: ColumnRef[];
  
  /**
   * Method to update the query
   */
  updateQuery: (query: Query) => void;
  
  /**
   * Class name for the header
   */
  className?: string;
  
  /**
   * Data name to display
   */
  dataName: string;
  
  /**
   * Row count to display
   */
  rowCount: number;
  
  /**
   * Method to toggle the AI chat panel
   * @deprecated No longer needed as AI chat is now in a permanent sidebar
   */
  onToggleAIChat?: () => void;

  /**
   * Method to execute the current query
   */
  executeQuery: () => Promise<void>;
  
  /**
   * Current expansion state of the query builder
   */
  isExpanded?: boolean;
  
  /**
   * Callback when the expansion state changes
   */
  onExpandChange?: (expanded: boolean) => void;

  /**
   * Optional content to render in the top-right area of the header
   * This can include export buttons, save buttons, or any other controls
   */
  topRightSlot?: React.ReactNode;

  /**
   * The view from the last executed query
   * This is used for exporting data without pagination
   */
  lastExecutedView?: TableView | null;
  
  /**
   * Optional callback when the upgrade link is clicked
   * Used to show the upgrade modal for non-logged-in users
   */
  onUpgradeClick?: (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'create_dataset' }) => void;
}

export interface SelectQueryConfigProps {
  query: Query;
  /**
   * Original table columns for selection
   * These are the columns from the original table, not the result of a previous query
   */
  originalColumns: ColumnRef[];
  updateQuery: (query: Query) => void;
  /**
   * Optional callback when a selected item is clicked
   * Used to collapse the query builder
   */
  onSelectedItemClick?: () => void;
}

export interface AggregateQueryConfigProps {
  query: Query;
  /**
   * Available columns (not used for aggregation)
   * @deprecated Use originalColumns instead for aggregation
   */
  columns?: ColumnRef[];
  /**
   * Original table columns (for aggregation)
   * These are the columns from the original table, not the result of a previous aggregation
   */
  originalColumns?: ColumnRef[];
  updateQuery: (query: Query) => void;
}

export interface SQLQueryConfigProps {
  query: Query;
  updateQuery: (query: Query) => void;
  columns: ColumnRef[];
}

// Filter condition interface
export interface FilterCondition {
  column: string;
  operation: string;
  value: string;
  conjunction?: 'AND' | 'OR';
}

// Define filter operations for different data types
export const STRING_OPERATIONS = [
  { label: 'Contains', value: 'contains', sqlTemplate: (col: string, val: string) => `${col} LIKE '%${val}%'` },
  { label: 'Starts with', value: 'startsWith', sqlTemplate: (col: string, val: string) => `${col} LIKE '${val}%'` },
  { label: 'Ends with', value: 'endsWith', sqlTemplate: (col: string, val: string) => `${col} LIKE '%${val}'` },
  { label: 'Equals', value: 'equals', sqlTemplate: (col: string, val: string) => `${col} = '${val}'` },
  { label: 'Not equals', value: 'notEquals', sqlTemplate: (col: string, val: string) => `${col} <> '${val}'` },
];

export const NUMBER_OPERATIONS = [
  { label: 'Equals', value: 'equals', sqlTemplate: (col: string, val: string) => `${col} = ${val}` },
  { label: 'Not equals', value: 'notEquals', sqlTemplate: (col: string, val: string) => `${col} <> ${val}` },
  { label: 'Greater than', value: 'gt', sqlTemplate: (col: string, val: string) => `${col} > ${val}` },
  { label: 'Greater than or equal', value: 'gte', sqlTemplate: (col: string, val: string) => `${col} >= ${val}` },
  { label: 'Less than', value: 'lt', sqlTemplate: (col: string, val: string) => `${col} < ${val}` },
  { label: 'Less than or equal', value: 'lte', sqlTemplate: (col: string, val: string) => `${col} <= ${val}` },
];

export const DATE_OPERATIONS = [
  { label: 'Equals', value: 'equals', sqlTemplate: (col: string, val: string) => `${col} = '${val}'` },
  { label: 'Not equals', value: 'notEquals', sqlTemplate: (col: string, val: string) => `${col} <> '${val}'` },
  { label: 'After', value: 'after', sqlTemplate: (col: string, val: string) => `${col} > '${val}'` },
  { label: 'After or on', value: 'afterOrOn', sqlTemplate: (col: string, val: string) => `${col} >= '${val}'` },
  { label: 'Before', value: 'before', sqlTemplate: (col: string, val: string) => `${col} < '${val}'` },
  { label: 'Before or on', value: 'beforeOrOn', sqlTemplate: (col: string, val: string) => `${col} <= '${val}'` },
];

/**
 * Determines which aggregation operations are valid for a given column type
 * @param column The column reference to check
 * @returns Array of valid aggregation operations for the column
 */
export function getValidAggregationsForColumn(column: ColumnRef): AggregateOperationEnum[] {
  // All columns support COUNT
  const validOperations: AggregateOperationEnum[] = [AggregateOperationEnum.COUNT];
  
  // Get the column type as string
  const columnType = column.schema.type.toString();
  
  // Numeric columns support SUM, AVG, MIN, MAX
  if (isNumericType(columnType)) {
    validOperations.push(
      AggregateOperationEnum.SUM,
      AggregateOperationEnum.AVG,
      AggregateOperationEnum.MIN,
      AggregateOperationEnum.MAX
    );
  }
  
  // String columns support MIN, MAX (for alphabetical comparison)
  else if (isStringType(columnType)) {
    validOperations.push(
      AggregateOperationEnum.MIN,
      AggregateOperationEnum.MAX
    );
    // Note: String columns do NOT support SUM or AVG
  }
  
  // Date columns support MIN, MAX
  else if (isDateType(columnType)) {
    validOperations.push(
      AggregateOperationEnum.MIN,
      AggregateOperationEnum.MAX
    );
    // Note: Date columns do NOT support SUM or AVG
  }
  
  // Boolean columns support SUM (treated as 0/1), MIN, MAX
  else if (isBooleanType(columnType)) {
    validOperations.push(
      AggregateOperationEnum.SUM,
      AggregateOperationEnum.MIN,
      AggregateOperationEnum.MAX
    );
    // Note: Boolean columns do NOT support AVG directly
  }
  
  return validOperations;
} 